import React, { useState, useEffect } from 'react';
import useModal from '@/hooks/useModal';
import { Button, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { Options } from '@/components/ui/combobox';
import clsx from 'clsx';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'next/navigation';
import { DatePickerCalendar } from '@/components/common/date-picker';

interface ItemsSelected {
	[key: string]: any[];
}
export interface FiltersStripe {
	[key: string]: Options[] | JSX.Element;
}

interface Props {
	filters: FiltersStripe;
	applyMultiple?: boolean;
	onClickApply: (values: ItemsSelected) => void;
	classNameCardContainer?: string;
}

export const MobileFilters: React.FC<Props> = ({
	filters,
	applyMultiple = true,
	classNameCardContainer,
	onClickApply,
}) => {
	const [selectedFilters, setSelectedFilters] = useState<ItemsSelected>({});
	const [appliedFilters, setAppliedFilter] = useState<ItemsSelected>({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { Modal, show, hide } = useModal();
	const searchParams = useSearchParams();

	useEffect(() => {
		if (isModalOpen) {
			const urlFilters: ItemsSelected = {};

			// Check for each specific filter in URL
			const selectedClientId = searchParams.get('clientId');
			const lawyerTaskStatus = searchParams.get('totalTaskLawyer');
			const segmentationId = searchParams.get('segmentId');
			const clientTaskStatus = searchParams.get('totalTaskClient');
			const messageFilterType = searchParams.get('messageFilter');
			const totalMessageCount = searchParams.get('totalMessage');
			const actionStatusLawyer = searchParams.get('actionStatusLawyer');
			const actionStatusClient = searchParams.get('actionStatusClient');
			const date = searchParams.get('date');

			if (selectedClientId) urlFilters['Client'] = selectedClientId.split(',');
			if (lawyerTaskStatus)
				urlFilters[`Lawyer's Tasks`] = lawyerTaskStatus.split(',');
			if (actionStatusLawyer)
				urlFilters[`Lawyer's Tasks`] = actionStatusLawyer.split(',');
			if (segmentationId) urlFilters['Segment'] = segmentationId.split(',');
			if (clientTaskStatus)
				urlFilters[`Client's Tasks`] = clientTaskStatus.split(',');
			if (actionStatusClient)
				urlFilters[`Client's Tasks`] = actionStatusClient.split(',');
			if (messageFilterType)
				urlFilters['Messages'] = messageFilterType.split(',');
			if (totalMessageCount)
				urlFilters['Messages'] = totalMessageCount.split(',');
			if (date) urlFilters['Date'] = [date];

			setSelectedFilters(urlFilters);
			setAppliedFilter(urlFilters);
		}
	}, [isModalOpen, searchParams]);

	const handleSelectFilters = (key: string, value: string) => {
		setSelectedFilters((prev) => {
			let filters = { ...prev };

			if (!filters[key]) {
				filters = {
					...filters,
					[key]: [value],
				};
			} else {
				if (applyMultiple) {
					filters = {
						...filters,
						[key]: filters[key].includes(value)
							? filters[key].filter((filter) => filter !== value)
							: [...filters[key], value],
					};
				} else {
					filters = {
						...filters,
						[key]: filters[key].includes(value)
							? filters[key].filter((filter) => filter !== value)
							: [value],
					};
				}
			}
			return filters;
		});
	};

	const handleCloseModal = (): void => {
		setIsModalOpen(false);
		hide();
	};

	const handleOpenModal = (): void => {
		setIsModalOpen(true);
		show();
	};

	const applyFilters = () => {
		setAppliedFilter(selectedFilters);
		onClickApply(selectedFilters);
		hide();
	};

	const clearAll = (index: string) => {
		setSelectedFilters((prev) => ({
			...prev,
			[index]: [],
		}));
	};

	const sectionsOpen = Object.entries(selectedFilters)
		.map(([key, value]) => (value.length > 0 ? key : ''))
		.filter((data) => data);
	const itemsOpen = sectionsOpen.length > 0 ? sectionsOpen : [''];
	const areFiltersEqual = (a: ItemsSelected, b: ItemsSelected) => {
		const aKeys = Object.keys(a);
		const bKeys = Object.keys(b);
		if (aKeys.length !== bKeys.length) return false;

		for (const key of aKeys) {
			const aValues = (a[key] || []).slice().sort();
			const bValues = (b[key] || []).slice().sort();
			if (!b[key] || aValues.length !== bValues.length) return false;
			for (let i = 0; i < aValues.length; i++) {
				if (aValues[i] !== bValues[i]) return false;
			}
		}
		return true;
	};

	const isApplyDisabled = React.useMemo(() => {
		// Check if there are any selected filters
		const hasSelectedFilters = Object.keys(selectedFilters).some(
			(key) => selectedFilters[key].length > 0
		);

		// Check if there are any applied filters
		const hasAppliedFilters = Object.keys(appliedFilters).some(
			(key) => appliedFilters[key].length > 0
		);

		// Step 1: Disable button if there are no selected filters (empty state)
		if (!hasSelectedFilters && !hasAppliedFilters) return true;

		// Step 2: Disable button if selected filters are the same as applied filters
		return areFiltersEqual(selectedFilters, appliedFilters);
	}, [selectedFilters, appliedFilters]);

	return (
		<>
			<Button
				decoration="ghost"
				size="fit"
				className="!p-2 !w-auto"
				onClick={handleOpenModal}
			>
				<Icon src={Icons.filter} className="!w-4 !h-4 relative" />
				{Object.keys(selectedFilters).length > 0 && (
					<span className="bg-main-blue rounded-full w-[10px] h-[10px] border border-white relative left-[-6px] top-[-3px]" />
				)}
			</Button>
			<Modal
				classNameCardContainer={clsx(
					'!w-full relative',
					classNameCardContainer
				)}
				withXButton
				title={'Filter by'}
				onClose={handleCloseModal}
				classNameContainerChildren="!pb-3"
			>
				<Accordion type="multiple" defaultValue={itemsOpen} className="w-full">
					{Object.entries(filters).map(([key, value], index) => {
						const isLastItem = index === Object.entries(filters).length - 1;
						const hasSelectedFilters = selectedFilters[key]?.length > 0;

						return (
							<AccordionItem key={`${index}-${value}`} value={key}>
								<AccordionTrigger className="!no-underline">
									<div className="flex items-center gap-2">
										<Typography type="regular-14">{key}</Typography>
										{hasSelectedFilters && (
											<span className="bg-main-blue rounded-full w-[10px] h-[10px] border border-white" />
										)}
									</div>
								</AccordionTrigger>
								<AccordionContent
									className={clsx('pr-2', {
										'pb-0': isLastItem,
									})}
								>
									<button
										onClick={() => clearAll(key)}
										className="font-normal flex items-center gap-2 "
									>
										<Icon src={Icons.broom} className="!w-6 !h-6" />
										Clear All
									</button>

									{!Array.isArray(value) && key === 'Date' && (
										<DatePickerCalendar
											isOpen={true}
											setValue={(value) => {
												handleSelectFilters(key, value ?? '');
											}}
											dateCurrent={new Date().toISOString()}
										/>
									)}
									{Array.isArray(value) &&
										value.map((option, index) => {
											const isLastItem = index === value.length - 1;
											return option?.options ? (
												<Accordion
													type="multiple"
													defaultValue={itemsOpen}
													key={`${index}-${option.label}`}
												>
													<AccordionItem
														value={option.title!}
														className={clsx('', {
															'border-0': isLastItem,
															'border-t mt-4': !isLastItem,
														})}
													>
														<AccordionTrigger className="!no-underline p-0 py-4">
															<Typography type="semibold-14">
																{option.title}
															</Typography>
														</AccordionTrigger>
														<AccordionContent className="!p-0">
															{option.options.map((item) => (
																<div
																	key={`${index}-${item.label}`}
																	className={clsx(
																		'flex items-center gap-2 w-full h-[48px] p-2',
																		{
																			'bg-gray-200': selectedFilters[
																				key
																			]?.includes(item.value),
																		}
																	)}
																	onClick={() => {
																		if (handleSelectFilters)
																			handleSelectFilters(
																				key,
																				item?.value ?? ''
																			);
																	}}
																>
																	<div>{item?.icon}</div>
																	<Typography
																		type="regular-14"
																		className="line-clamp-1 text-left w-full flex"
																	>
																		{item.label}
																		{selectedFilters[key]?.includes(
																			item.value
																		) && (
																			<span className="ml-auto">
																				<CheckIcon
																					className="w-5 h-5 text-black"
																					aria-hidden="true"
																				/>
																			</span>
																		)}
																	</Typography>
																</div>
															))}
														</AccordionContent>
													</AccordionItem>
												</Accordion>
											) : (
												<div
													key={index}
													className={clsx(
														'flex items-center mt-2 gap-2 w-full p-2 h-[48px]',
														{
															'bg-gray-200': selectedFilters[key]?.includes(
																option.value
															),
														}
													)}
													onClick={() => {
														if (handleSelectFilters)
															handleSelectFilters(key, option?.value ?? '');
													}}
												>
													<div>{option.icon && option.icon}</div>
													<Typography
														type="regular-14"
														className="line-clamp-1  truncate text-left p-2"
													>
														{option.label}
													</Typography>
													{selectedFilters[key]?.includes(option.value) && (
														<span className="ml-auto">
															<CheckIcon
																className="w-5 h-5 text-black"
																aria-hidden="true"
															/>
														</span>
													)}
												</div>
											);
										})}
								</AccordionContent>
							</AccordionItem>
						);
					})}

					<div className="w-full flex justify-center mt-4 ">
						<Button
							decoration="primary"
							onClick={applyFilters}
							className="!w-auto"
							disabled={isApplyDisabled}
						>
							Apply
						</Button>
					</div>
				</Accordion>
			</Modal>
		</>
	);
};
